import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { accessGroupMembers } from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import useGroupMemberships from 'modules/dashboard/hooks/useGroupMemberships'
import useMembers from 'modules/dashboard/hooks/useMembers'
import accessGroupMemberships from 'modules/dashboard/store/collections/AccessGroupMemberships'
import groups from 'modules/dashboard/store/collections/communities/Groups'
import type Membership from 'modules/teams/store/models/Membership'

interface Props {
  onClose: () => void
  groupId: number
  contractVersions: ContractVersions
  loading: boolean
}

function RemoveMembersGroup({ groupId, onClose, contractVersions, loading }: Props) {
  const [running, setRunning] = React.useState(false)

  const group = groups.find({
    id: groupId,
  })

  const accessGroup = group?.accessGroup
  const [memberIds, setMember, setMembers] = useMembers()
  const { loading: loadingGroupMemberships } = useGroupMemberships(groupId)

  const submit = async (closePortal: ClosePortalFn) => {
    if (!accessGroup) return

    setRunning(true)

    const { success, value: memberships } = await asyncAction(() =>
      group.rpc('remove_participants', {
        access_ids: memberIds,
      })
    )
    setRunning(false)

    if (success) {
      const membershipsIds = memberships.map((membership: Membership) => membership.id)
      accessGroupMemberships.remove(membershipsIds)
      closePortal()
    }
  }

  return (
    <Modal
      description={i18n.t('dashboard.groups.remove_members.description')}
      illustration="add-user"
      onClose={onClose}
      onSubmit={submit}
      title={i18n.t('dashboard.groups.remove_members.title')}
      footer={
        <Button disabled={!memberIds.length || running} submit>
          {i18n.t('dashboard.groups.remove_members.submit')}
        </Button>
      }
    >
      {loading || loadingGroupMemberships ? (
        <Loading />
      ) : (
        <UserSelector
          users={accessGroup ? accessGroupMembers(accessGroup) : []}
          onSelect={setMember}
          onSelectMultiple={setMembers}
          selectedIds={memberIds}
          contractVersions={contractVersions}
        />
      )}
    </Modal>
  )
}

export default observer(RemoveMembersGroup)
